import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import { Period, RestaurantWorkPeriod } from '../interfaces/period';

export const nullIfEmptyValidator = (control: AbstractControl) => {
  if (control.value === '') {
    control.setValue(null);
  }
  return null;
};

export const wifiNameAndPasswordValidator = (control: AbstractControl): ValidationErrors | null => {
  const wifiNameControl = control.get('wifiName');
  const wifiPasswordControl = control.get('wifiPassword');

  if (wifiPasswordControl?.value && !wifiNameControl?.value) {
    wifiNameControl?.setErrors({ required: true });
  } else {
    wifiNameControl?.setErrors(null);
  }
  return null;
};

export function passwordFormValidator(newPassControlName: string, oldPassControlName: string, enable: boolean): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    if (!enable) {
      return null;
    }

    const newPassCtrl = group.get(newPassControlName);
    const oldPassCtrl = group.get(oldPassControlName);

    if (newPassCtrl?.value && !oldPassCtrl?.value) {
      oldPassCtrl?.setErrors({ required: true });
      oldPassCtrl?.markAsDirty();
      oldPassCtrl?.markAsTouched();
    } else if (oldPassCtrl?.value && !newPassCtrl?.value) {
      newPassCtrl?.setErrors({ required: true });
      newPassCtrl?.markAsDirty();
      newPassCtrl?.markAsTouched();
    } else {
      removeError(oldPassCtrl, 'required');
      removeError(newPassCtrl, 'required');
    }

    return null;
  };
}

export function dayStartValidator(periods: Period[], id: number | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const root = control.root as AbstractControl;
    const dayStart = root.get('startDay')?.value;

    const hasConflict = periods.some(period => {
      const baseConflict = validateStartDay(dayStart, period, id);

      if (isRestaurantWorkPeriod(period)) {
        const periodWithOffset = applyPeriodOffset(period);

        const offsetConflict = validateStartDay(dayStart, periodWithOffset, id);
        return baseConflict || offsetConflict;
      }

      return baseConflict;
    });

    if (hasConflict) {
      return { dayStartConflict: true };
    }

    return null;
  };
}

export function timeStartValidator(periods: (Period | RestaurantWorkPeriod)[], id: number | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const root = control.root as AbstractControl;
    const dayStart = root.get('startDay')?.value;
    const timeStart = control.value;

    if (timeStart && dayStart && periods) {
      const hasConflict = periods.some((period) => {
        const baseConflict = validateStartTime(timeStart, dayStart, period, id);

        if (isRestaurantWorkPeriod(period)) {
          const periodWithOffset = applyPeriodOffset(period);

          const offsetConflict = validateStartTime(timeStart, dayStart, periodWithOffset, id);
          return baseConflict || offsetConflict;
        }

        return baseConflict;
      });

      if (hasConflict) {
        return { timeStartConflict: true };
      }
    }

    return null;
  };
}

export function startOffsetValidator(periods: Period[], id: number | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const root = control.root as AbstractControl;
    const dayStart = root.get('startDay')?.value;
    const timeStart = root.get('startTime')?.value;
    const dayEnd = root.get('endDay')?.value;
    const timeEnd = root.get('endTime')?.value;
    const startOffset = control.value;
    const adjustedPeriod = applyOffsetWithDay(timeStart, dayStart, startOffset);

    if (timeStart && dayStart && periods && adjustedPeriod) {
      const selfConflict =
        (timeStart !== adjustedPeriod.time) &&
        ((dayStart === dayEnd || adjustedPeriod.day === dayEnd)
          && adjustedPeriod.time > timeEnd)
        || ( dayStart === dayEnd && adjustedPeriod.day > dayEnd);
      const hasConflict = periods.some(period => {
        const baseConflict =  validateStartTime(adjustedPeriod.time, adjustedPeriod.day, period, id);

        if (isRestaurantWorkPeriod(period)) {
          const periodWithOffset = applyPeriodOffset(period);

          const offsetConflict = validateStartTime(adjustedPeriod.time, adjustedPeriod.day, periodWithOffset, id);
          return baseConflict || offsetConflict || selfConflict;
        }

        return baseConflict || selfConflict;
      });

      if (hasConflict) {
        return { startOffsetConflict: true };
      }
    }

    return null;
  };
}

export function dayEndValidator(periods: Period[], id: number | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const root = control.root as AbstractControl;
    const dayStart = root.get('startDay')?.value;
    const dayEnd = root.get('endDay')?.value;

    const hasConflict = periods.some(period => {
      const baseConflict = validateEndDay(dayStart, dayEnd, period, id);

      if (isRestaurantWorkPeriod(period)) {
        const periodWithOffset = applyPeriodOffset(period);

        const offsetConflict = validateEndDay(dayStart, dayEnd, periodWithOffset, id);
        return baseConflict || offsetConflict;
      }

      return baseConflict;
    });

    if (hasConflict) {
      return { dayEndConflict: true };
    }

    return null;
  };
}


export function timeEndValidator(periods: Period[], id: number | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const root = control.root as AbstractControl;
    const dayStart = root.get('startDay')?.value;
    const dayEnd = root.get('endDay')?.value;
    const timeStart = root.get('startTime')?.value;
    const timeEnd = control.value;

    if (dayStart !== null && dayEnd !== null && dayStart === dayEnd) {
      if (timeEnd !== null && timeStart !== null) {
        if (timeStart === timeEnd) {
          return { timeEndEqualToStart: true };
        }
      }
    }

    if (periods) {
      const hasConflict = periods.some((period) => {
        if (id && period.id === id) {
          return false;
        }

        const baseConflict = validateEndTime(dayStart, timeStart, dayEnd, timeEnd, period);

        if (isRestaurantWorkPeriod(period)) {
          const periodWithOffset = applyPeriodOffset(period);

          const offsetConflict = validateEndTime(dayStart, timeStart, dayEnd, timeEnd, periodWithOffset);
          return baseConflict || offsetConflict;
        }

        return baseConflict;
      });

      if (hasConflict) {
        return { timeEndConflict: true };
      }
    }

    return null;
  };
}

export function endOffsetValidator(periods: Period[], id: number | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const root = control.root as AbstractControl;
    const dayStart = root.get('startDay')?.value;
    const dayEnd = root.get('endDay')?.value;
    const timeStart = root.get('startTime')?.value;
    const timeEnd = root.get('endTime')?.value;
    const endOffset = control.value;
    const adjustedEnd = applyOffsetWithDay(timeEnd, dayEnd, endOffset);

    if (dayStart !== null && dayEnd !== null && dayStart === dayEnd) {
      if (timeEnd !== null && timeStart !== null) {
        if (timeStart === timeEnd) {
          return { timeEndEqualToStart: true };
        }
      }
    }

    if (periods) {
      const selfConflict =
        ((dayStart === dayEnd || adjustedEnd.day === dayStart) && adjustedEnd.time < timeStart)
        || ( dayStart === dayEnd && adjustedEnd.day < dayStart);

      if (selfConflict) {
        return { endOffsetConflict: true };
      }

      const hasConflict = periods.some((period) => {
        if (id && period.id === id) {
          return false;
        }

        const baseConflict = validateEndTime(dayStart, timeStart, adjustedEnd.day, adjustedEnd.time, period);

        if (isRestaurantWorkPeriod(period)) {
          const periodWithOffset = applyPeriodOffset(period);

          const offsetConflict = validateEndTime(dayStart, timeStart, adjustedEnd.day, adjustedEnd.time, periodWithOffset);
          return baseConflict || offsetConflict;
        }

        return baseConflict;
      });

      if (hasConflict) {
        return { endOffsetConflict: true };
      }
    }

    return null;
  };
}



function removeError(control: AbstractControl | null, errorKey: any): void {
  const errors: any = control?.errors;
  let processedErrors: ValidationErrors | null = {};

  if (errors && errors instanceof Object) {
    Object.keys(errors).forEach((key: string) => {
      if (key !== errorKey && processedErrors && errors) {
        processedErrors[key] = errors[key];
      }
    });
  }

  if (Object.keys(processedErrors).length < 1) {
    processedErrors = null;
  }

  control?.setErrors(processedErrors);
}

function validateStartDay(dayStart: number, period: Period, id: number | null): boolean {
  if (id && period.id === id) {
    return false;
  }

  if (period.endDay < period.startDay) {
    return dayStart < period.endDay || dayStart > period.startDay;
  }

  return dayStart > period.startDay && dayStart < period.endDay;
}

function validateStartTime(timeStart: string, dayStart: number, period: Period, id: number | null): boolean {

    if (id && period.id === id) {
      return false;
    }

    if (period.endDay < period.startDay) {
      const crossesStartDay =
        dayStart === period.startDay && timeStart >= period.startTime;
      const crossesEndDay =
        dayStart === period.endDay && timeStart <= period.endTime;
      return crossesStartDay || crossesEndDay;
    }

    const withinSameDay =
      dayStart === period.startDay && dayStart === period.endDay &&
      timeStart >= period.startTime && timeStart <= period.endTime;

    const withinRange =
      dayStart > period.startDay && dayStart < period.endDay;

    const atStartBoundary =
      dayStart === period.startDay &&
      timeStart >= period.startTime &&
      !(dayStart === period.endDay && timeStart > period.endTime);

    const atEndBoundary =
      dayStart === period.endDay &&
      timeStart <= period.endTime &&
      !(dayStart === period.startDay && timeStart < period.startTime);

    return withinSameDay || withinRange || atStartBoundary || atEndBoundary;
}

function validateEndDay(dayStart: number, dayEnd: number, period: Period, id: number | null): boolean {
  if (id && period.id === id) {
    return false;
  }

  if (period.endDay < period.startDay) {
    return dayEnd > period.startDay || dayEnd < period.endDay;
  }

  if (dayEnd < dayStart) {
    return dayEnd > period.startDay || dayEnd > period.endDay;
  }

  const isInside =
    ((dayStart > period.startDay && dayEnd < period.endDay) && dayEnd > dayStart) ||
    (dayStart < period.startDay && dayEnd > period.endDay);

  const overlaps = dayStart < period.endDay && dayEnd > period.startDay;

  return isInside || overlaps;
}

function validateEndTime(
  dayStart: number,
  timeStart: string,
  dayEnd: number,
  timeEnd: string,
  period: Period,
): boolean {

  if (period.endDay < period.startDay) {
    if (dayStart < period.endDay || dayStart > period.startDay) {
      return true;
    }

    if (dayEnd >= period.startDay || dayEnd <= period.endDay) {
      return timeEnd >= period.startTime || timeEnd <= period.endTime;
    }

    return false;
  }

  if (dayEnd < dayStart || (dayEnd === dayStart && timeStart > timeEnd)) {
    if (dayEnd > period.startDay || dayStart < period.endDay) {
      return true;
    } else if (dayEnd === period.startDay && timeEnd >= period.startTime) {
      return true;
    } else if (dayStart === period.endDay && timeStart <= period.endTime) {
      return true;
    }
  }

  const periodWraps = period.startDay > period.endDay || (period.startDay === period.endDay && period.startTime > period.endTime);
  const currentWraps = dayStart > dayEnd || (dayStart === dayEnd && timeStart > timeEnd);

  let overlaps = false;

  if (!periodWraps && !currentWraps) {
    overlaps =
      period.startDay <= dayEnd &&
      period.endDay >= dayStart &&
      !(period.endDay === dayStart && period.endTime <= timeStart) &&
      !(period.startDay === dayEnd && period.startTime >= timeEnd);
  } else if (periodWraps && !currentWraps) {
    overlaps =
      (dayStart > period.startDay || (dayStart === period.startDay && timeStart >= period.startTime)) ||
      (dayEnd < period.endDay || (dayEnd === period.endDay && timeEnd <= period.endTime));
  } else if (!periodWraps && currentWraps) {
    overlaps =
      (period.startDay < dayEnd || (period.startDay === dayEnd && period.startTime < timeEnd)) ||
      (period.endDay > dayStart || (period.endDay === dayStart && period.endTime > timeStart));
  } else {
    overlaps = true;
  }


  const isContained =
    dayStart <= period.startDay && dayEnd >= period.endDay &&
    timeStart <= period.startTime && timeEnd >= period.endTime;

  return overlaps || isContained;
}

function applyOffsetWithDay(
  time: string,
  day: number,
  offsetSeconds: number
): { time: string; day: number } {
  const [hours, minutes] = time.split(':').map(Number);
  const totalSeconds = (hours * 60 + minutes) * 60 + offsetSeconds;

  const totalSecondsInWeek = 7 * 24 * 60 * 60;
  const adjustedSeconds = (totalSeconds + totalSecondsInWeek) % totalSecondsInWeek;

  const newDay = ((day - 1 + Math.floor(adjustedSeconds / (24 * 60 * 60))) % 7 + 7) % 7 + 1;
  const remainder = adjustedSeconds % (24 * 60 * 60);
  const newHour = Math.floor(remainder / 3600);
  const newMinute = Math.floor((remainder % 3600) / 60);

  return {
    time: `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`,
    day: newDay
  };
}

function applyPeriodOffset(period: RestaurantWorkPeriod): Period {
  const offsetStart = period.devicesAutoOn
    ? applyOffsetWithDay(period.startTime, period.startDay, period.devicesAutoOnOffset)
    : { time: period.startTime, day: period.startDay };

  const offsetEnd = period.devicesAutoOff
    ? applyOffsetWithDay(period.endTime, period.endDay, period.devicesAutoOffOffset)
    : { time: period.endTime, day: period.endDay };

  return  {
    ...period,
    startTime: offsetStart.time,
    startDay: offsetStart.day,
    endTime: offsetEnd.time,
    endDay: offsetEnd.day
  };
}

function isRestaurantWorkPeriod(period: Period): period is RestaurantWorkPeriod {
  return (
    (period as RestaurantWorkPeriod).devicesAutoOn !== undefined &&
    (period as RestaurantWorkPeriod).devicesAutoOnOffset !== undefined &&
    (period as RestaurantWorkPeriod).devicesAutoOff !== undefined &&
    (period as RestaurantWorkPeriod).devicesAutoOffOffset !== undefined
  );
}
